'use client';

import { AlignCenter, AlignJustify, AlignLeft, AlignRight, Bold, ChevronDown, Code2, FileText, Highlighter, History, ImagePlus, Italic, Link2, List, ListOrdered, Plus, Strikethrough, Underline, Video, X } from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent, type ClipboardEvent as ReactClipboardEvent, type CSSProperties, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import CommentLogin, { type DemoUser } from './comment-login';
import { readWikiToken, readWikiUser, writeWikiUser } from '../lib/wiki-auth';
import { defaultHomepageConfig, fetchHomepageConfig } from '../lib/homepage-config';
import { uploadToObs } from '../lib/obs/client-upload';
import {
  wikiGameHomePath,
  wikiGuideEditPath,
  wikiGuidePostPath,
  wikiGuidesPath,
  wikiShellPath,
} from '../lib/spa-routes';
import { cleanLegacyArticleMarkup } from '../lib/guide-article';
import { rewriteHtmlImgToWebp } from '../lib/config/image-webp';
import './create-post-page.css';
import './article-body-shared.css';

type AvailableGame = { key: string; name: string; domain: string };

const postTypes = ['攻略贴', '讨论贴', '情报贴', '创作贴'] as const;
const secondaryCategories: Record<(typeof postTypes)[number], string[]> = {
  攻略贴: defaultHomepageConfig.guideCategories.filter(category => category !== '全部'),
  讨论贴: ['阵容讨论', '角色讨论', '玩法讨论', '求助问答'],
  情报贴: ['版本情报', '活动资讯', '游戏公告'],
  创作贴: ['同人创作', '视频分享', '截图分享'],
};

const selectionTextColors = [
  { name: '黑色', value: '#1f2329' },
  { name: '灰色', value: '#8f959e' },
  { name: '红色', value: '#d83931' },
  { name: '橙色', value: '#d46b08' },
  { name: '黄色', value: '#ad7a00' },
  { name: '绿色', value: '#2b8a3e' },
  { name: '蓝色', value: '#245bdb' },
  { name: '紫色', value: '#7a45d1' },
];
const selectionHighlightColors = [
  { name: '无背景', value: 'transparent' },
  { name: '浅灰', value: '#f2f3f5' },
  { name: '浅红', value: '#fde2e2' },
  { name: '浅橙', value: '#ffe7ba' },
  { name: '浅黄', value: '#fff3a3' },
  { name: '浅绿', value: '#d9f4d7' },
  { name: '浅蓝', value: '#dbeafe' },
  { name: '浅紫', value: '#e9ddff' },
  { name: '深灰', value: '#d0d3d8' },
  { name: '深红', value: '#ff8f80' },
  { name: '深橙', value: '#ffb45f' },
  { name: '深黄', value: '#ffe044' },
  { name: '深绿', value: '#62d26f' },
  { name: '深蓝', value: '#78a9ff' },
  { name: '深紫', value: '#b99cff' },
  { name: '青色', value: '#72d4c5' },
];

const escapeHtml = (value: string) => value.replace(/[&<>"']/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[character] || character));
const pastedResourceExtensions = new Set([
  'jpg', 'jpeg', 'png', 'webp', 'gif', 'avif', 'mp4', 'm4v', 'webm', 'mov',
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'zip',
]);
const pastedMimeExtensions: Record<string, string> = {
  'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif', 'image/avif': 'avif',
  'video/mp4': 'mp4', 'video/webm': 'webm', 'video/quicktime': 'mov',
  'application/pdf': 'pdf', 'application/zip': 'zip',
  'application/msword': 'doc', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-powerpoint': 'ppt', 'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
};

type ActivityPost = { gameKey: string; id: string; type: string; title: string; excerpt: string; status: string; updatedAt: string };
type DraftRevision = { id: string; savedAt: string; title: string; category: string; postType: string; wordCount: number; excerpt: string; signature: string; changes: string[] };
type ImageControls = { top: number; left: number; width: number; height: number };
type EditorSelectionPoint = { path: number[]; offset: number };
type EditorUndoSnapshot = {
  html: string;
  selection: { start: EditorSelectionPoint; end: EditorSelectionPoint } | null;
};
type ScrollPosition = {
  windowX: number;
  windowY: number;
  elements: Array<{ element: HTMLElement; left: number; top: number }>;
};
type EditorDialog =
  | { kind: 'link'; label: string; url: string; error?: string }
  | { kind: 'table'; rows: string; columns: string; error?: string }
  | { kind: 'button'; label: string; url: string; error?: string };

const directEditorBlock = (editor: HTMLElement, node: Node, offset = 0) => {
  if (node === editor) {
    const children = Array.from(editor.children).filter((child): child is HTMLElement => child instanceof HTMLElement);
    if (!children.length) return null;
    return children[Math.min(offset, children.length - 1)] || children[children.length - 1];
  }
  let element = node.nodeType === Node.ELEMENT_NODE ? node as HTMLElement : node.parentElement;
  const columns = element?.closest<HTMLElement>('.document-columns');
  if (columns && editor.contains(columns)) {
    let column: HTMLElement | null = element;
    while (column?.parentElement && column.parentElement !== columns) column = column.parentElement;
    let block: HTMLElement | null = element;
    while (block && block !== column) {
      if (block.matches('p,h1,h2,h3,h4,blockquote,pre,ol,ul,figure,hr,.document-callout,.document-table-wrap')) return block;
      block = block.parentElement;
    }
    return column;
  }
  while (element?.parentElement && element.parentElement !== editor) element = element.parentElement;
  return element?.parentElement === editor ? element : null;
};

const isEmptyEditorBlock = (block: HTMLElement) => {
  const text = (block.textContent || '').replace(/\u200b/g, '').trim();
  const hasEmbeddedContent = Boolean(block.querySelector('img,video,audio,table,hr,input,iframe,.document-columns,.document-callout,.document-synced-block,.document-attachment'));
  return !text && !hasEmbeddedContent;
};

const isAtomicEditorBlock = (block: HTMLElement) =>
  !block.matches('.document-callout') && (
    block.matches('figure,.document-table-wrap,.document-columns,.document-synced-block,.document-attachment') ||
    Boolean(block.querySelector('img,video,audio,table,iframe'))
  );

const captureEditorSelection = (editor: HTMLElement): EditorUndoSnapshot['selection'] => {
  const selection = window.getSelection();
  if (!selection?.rangeCount) return null;
  const range = selection.getRangeAt(0);
  if (!editor.contains(range.startContainer) || !editor.contains(range.endContainer)) return null;
  const point = (node: Node, offset: number): EditorSelectionPoint => {
    const path: number[] = [];
    while (node !== editor && node.parentNode) {
      path.unshift(Array.prototype.indexOf.call(node.parentNode.childNodes, node) as number);
      node = node.parentNode;
    }
    return { path, offset };
  };
  return { start: point(range.startContainer, range.startOffset), end: point(range.endContainer, range.endOffset) };
};

const restoreEditorSelection = (editor: HTMLElement, snapshot: EditorUndoSnapshot) => {
  const resolve = ({ path, offset }: EditorSelectionPoint) => {
    let node: Node = editor;
    for (const index of path) node = node.childNodes[Math.min(index, node.childNodes.length - 1)] || node;
    const limit = node.nodeType === Node.TEXT_NODE ? (node.textContent?.length || 0) : node.childNodes.length;
    return { node, offset: Math.min(offset, limit) };
  };
  const start = resolve(snapshot.selection?.start || { path: [], offset: editor.childNodes.length });
  const end = resolve(snapshot.selection?.end || { path: [], offset: editor.childNodes.length });
  const range = document.createRange();
  try {
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
  } catch {
    range.selectNodeContents(editor);
    range.collapse(false);
  }
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
  return range;
};

const captureScrollPosition = (element: HTMLElement): ScrollPosition => {
  const elements: ScrollPosition['elements'] = [];
  let parent = element.parentElement;
  while (parent) {
    if (parent.scrollHeight > parent.clientHeight || parent.scrollWidth > parent.clientWidth) {
      elements.push({ element: parent, left: parent.scrollLeft, top: parent.scrollTop });
    }
    parent = parent.parentElement;
  }
  return { windowX: window.scrollX, windowY: window.scrollY, elements };
};

const restoreScrollPosition = (position: ScrollPosition) => {
  position.elements.forEach(({ element, left, top }) => {
    element.scrollLeft = left;
    element.scrollTop = top;
  });
  window.scrollTo(position.windowX, position.windowY);
};

const pastedTags = new Set([
  'A', 'B', 'BLOCKQUOTE', 'BR', 'CODE', 'DEL', 'DIV', 'EM', 'FIGCAPTION',
  'FIGURE', 'H1', 'H2', 'H3', 'H4', 'HR', 'I', 'IMG', 'LI', 'OL', 'P',
  'PRE', 'S', 'SPAN', 'STRONG', 'SUB', 'SUP', 'TABLE', 'TBODY', 'TD',
  'TH', 'THEAD', 'TR', 'U', 'UL', 'VIDEO',
]);
const pastedStyleProperties = new Set([
  'color', 'background-color', 'font-family', 'font-size', 'font-weight',
  'font-style', 'text-decoration', 'text-align', 'vertical-align', 'white-space',
]);
const pastedInlineTags = new Set(['A', 'B', 'CODE', 'DEL', 'EM', 'I', 'S', 'SPAN', 'STRONG', 'SUB', 'SUP', 'U']);

const safePastedUrl = (value: string, allowDataImage = false) => {
  const url = value.trim();
  if (allowDataImage && /^data:image\/(png|gif|jpe?g|webp|avif);base64,/i.test(url)) return url;
  if (/^data:video\/(mp4|webm|quicktime);base64,/i.test(url)) return url;
  if (/^(https?:|mailto:|\/|#)/i.test(url) && !url.startsWith('//')) return url;
  return '';
};

/** Convert clipboard HTML to a small, editor-compatible and safe formatting subset. */
const cleanPastedHtml = (html: string) => {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const cleanChildren = (parent: Node) => {
    Array.from(parent.childNodes).forEach(node => {
      if (node.nodeType === Node.COMMENT_NODE) {
        node.remove();
        return;
      }
      // Clipboard SVG/MathML elements are not HTMLElements. Leaving them in place
      // lets oversized icons from the legacy editor render inside the document.
      if (node.nodeType === Node.ELEMENT_NODE && !(node instanceof HTMLElement)) {
        node.remove();
        return;
      }
      if (!(node instanceof HTMLElement)) return;
      const tag = node.tagName;
      // Word/legacy Wiki list markers are presentation artifacts, not content.
      if (/mso-list\s*:\s*ignore/i.test(node.getAttribute('style') || '') || /MsoListParagraph/i.test(node.className)) {
        node.remove();
        return;
      }
      if (['SCRIPT', 'STYLE', 'LINK', 'META', 'TITLE', 'XML', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'INPUT', 'BUTTON'].includes(tag)) {
        node.remove();
        return;
      }
      const pastedStyle = (node as HTMLElement).getAttribute('style') || '';
      const pastedHref = node.getAttribute('href');
      const pastedSrc = node.getAttribute('src');
      const pastedSourceSrc = tag === 'VIDEO' ? node.querySelector('source')?.getAttribute('src') : null;
      const pastedAlt = node.getAttribute('alt');
      const pastedSpans = {
        colspan: node.getAttribute('colspan'),
        rowspan: node.getAttribute('rowspan'),
      };
      cleanChildren(node);
      if (!pastedTags.has(tag)) {
        node.replaceWith(...Array.from(node.childNodes));
        return;
      }
      Array.from(node.attributes).forEach(attribute => node.removeAttribute(attribute.name));
      if (tag === 'A') {
        const href = safePastedUrl(pastedHref || '');
        if (href) node.setAttribute('href', href);
      }
      if (tag === 'IMG') {
        const src = safePastedUrl(pastedSrc || '', true);
        if (src) node.setAttribute('src', src);
        else { node.remove(); return; }
        if (pastedAlt) node.setAttribute('alt', pastedAlt.slice(0, 300));
      }
      if (tag === 'VIDEO') {
        const src = safePastedUrl(pastedSrc || pastedSourceSrc || '');
        if (src) node.setAttribute('src', src);
        else { node.remove(); return; }
        node.setAttribute('controls', '');
        node.setAttribute('preload', 'metadata');
      }
      if (tag === 'TD' || tag === 'TH') {
        for (const name of ['colspan', 'rowspan'] as const) {
          const value = Number(pastedSpans[name]);
          if (Number.isInteger(value) && value >= 1 && value <= 100) node.setAttribute(name, String(value));
        }
      }
      const source = parsed.createElement('span');
      source.setAttribute('style', pastedStyle);
      const style: string[] = [];
      for (const property of pastedStyleProperties) {
        const value = source.style.getPropertyValue(property).trim();
        if (
          value && value.length <= 160 &&
          !/url\s*\(|expression\s*\(|javascript:/i.test(value) &&
          (property !== 'background-color' || pastedInlineTags.has(tag))
        ) style.push(`${property}:${value}`);
      }
      if (style.length) node.setAttribute('style', style.join(';'));
    });
  };
  cleanChildren(parsed.body);
  return parsed.body.innerHTML;
};

const escapePastedText = (value: string) => value.replace(/[&<>"']/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[character]!)).replace(/\r\n?/g, '\n').replace(/\n/g, '<br>');

const normalizeEmptyEditorBlocks = (editor: HTMLElement) => {
  editor.querySelectorAll('svg,math').forEach(node => node.remove());
  const hasDocumentContent = Array.from(editor.childNodes).some(node => {
    if (node.nodeType === Node.TEXT_NODE) return Boolean(node.textContent?.replace(/\u200b/g, '').trim());
    if (!(node instanceof HTMLElement) || node.tagName === 'BR') return false;
    return !isEmptyEditorBlock(node);
  });
  const hasParagraph = Array.from(editor.children).some(child => child instanceof HTMLParagraphElement);
  if (!hasDocumentContent && !hasParagraph) {
    const emptyParagraph = document.createElement('p');
    emptyParagraph.appendChild(document.createElement('br'));
    editor.replaceChildren(emptyParagraph);
    const selection = window.getSelection();
    if (!selection?.anchorNode || !emptyParagraph.contains(selection.anchorNode)) {
      const range = document.createRange();
      range.setStart(emptyParagraph, 0);
      range.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }
  Array.from(editor.children).forEach(child => {
    if (!(child instanceof HTMLElement)) return;
    const isTextBlock = child.matches('p,div,h1,h2,h3,h4');
    child.classList.toggle('document-empty-block', isTextBlock && isEmptyEditorBlock(child));
  });
};

export default function CreatePostPage({
  mode = 'post',
  editPostId,
  preset,
  initialGameKey,
  initialGameDomain,
  availableGames,
  lockGame = false,
}: {
  mode?: 'post' | 'guide';
  editPostId?: string;
  preset?: 'announcement' | 'information';
  initialGameKey: string;
  /** URL 段用的 domain_key；缺省时从 availableGames 解析 */
  initialGameDomain?: string;
  availableGames: AvailableGame[];
  lockGame?: boolean;
}) {
  const isGuide = mode === 'guide';
  const defaultPostType: (typeof postTypes)[number] = isGuide
    ? '攻略贴'
    : preset === 'announcement' || preset === 'information'
      ? '情报贴'
      : '讨论贴';
  const defaultSubcategory = preset === 'announcement'
    ? '游戏公告'
    : preset === 'information'
      ? '活动资讯'
    : secondaryCategories[defaultPostType][0];
  const primaryChannel = isGuide
    ? '攻略'
    : preset === 'announcement'
      ? '公告'
      : preset === 'information'
        ? '资讯'
        : '社区';
  const draftKey = editPostId
    ? `game-wiki-post-edit-draft-${initialGameKey}-${editPostId}`
    : `${isGuide ? 'game-wiki-guide-draft' : 'game-wiki-community-post-draft'}-${initialGameKey}`;
  const [user, setUser] = useState<DemoUser | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [gameKey, setGameKey] = useState(initialGameKey);
  const gameDomain =
    availableGames.find((game) => game.key === gameKey)?.domain ||
    initialGameDomain ||
    gameKey;
  const domainForGameKey = (key: string) =>
    availableGames.find((game) => game.key === key)?.domain ||
    (key === gameKey ? gameDomain : key);
  const primaryChannelURL = primaryChannel === '攻略'
    ? wikiGuidesPath(gameDomain)
    : `${wikiShellPath(gameDomain, 'dcommunity')}?category=${encodeURIComponent(primaryChannel)}`;
  const [postType, setPostType] = useState<(typeof postTypes)[number]>(defaultPostType);
  const [guideCategories, setGuideCategories] = useState(secondaryCategories.攻略贴);
  const availableSecondary = postType === '攻略贴' ? guideCategories : secondaryCategories[postType];
  const [subcategory, setSubcategory] = useState(defaultSubcategory);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [outline, setOutline] = useState<Array<{ id: string; level: number; title: string }>>([]);
  const [status, setStatus] = useState('');
  const [editReady, setEditReady] = useState(!editPostId);
  const [blockHandlePosition, setBlockHandlePosition] = useState<{ top: number; left: number } | null>(null);
  const [blockMenuOpen, setBlockMenuOpen] = useState(false);
  const [selectionToolbar, setSelectionToolbar] = useState<{ top: number; left: number } | null>(null);
  const [imageControls, setImageControls] = useState<ImageControls | null>(null);
  const [selectionMenu, setSelectionMenu] = useState<'style' | 'align' | 'size' | 'color' | null>(null);
  const [drawer, setDrawer] = useState<'posts' | 'revisions' | null>(null);
  const [myPosts, setMyPosts] = useState<ActivityPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState('');
  const [draftRevisions, setDraftRevisions] = useState<DraftRevision[]>([]);
  const [titlePinned, setTitlePinned] = useState(false);
  const [editorDialog, setEditorDialog] = useState<EditorDialog | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const editorShellRef = useRef<HTMLDivElement>(null);
  const selectedImageRef = useRef<HTMLImageElement | null>(null);
  const imageResizeRef = useRef<{ pointerId: number; startX: number; startY: number; startWidth: number; aspectRatio: number; corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' } | null>(null);
  const blockControlsRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const lastCaretClickRef = useRef<{ node: Node; offset: number; clientY: number } | null>(null);
  const editorPointerDownRef = useRef(false);
  const blockInsertionRangeRef = useRef<Range | null>(null);
  const selectedTextRangeRef = useRef<Range | null>(null);
  const dialogRangeMarkersRef = useRef<{ start: Comment; end: Comment } | null>(null);
  const tableScrollPositionRef = useRef<ScrollPosition | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const draftHistoryReadyRef = useRef(false);
  const undoHistoryRef = useRef<{ entries: EditorUndoSnapshot[]; index: number }>({ entries: [], index: -1 });
  const pendingInputKindRef = useRef<string | null>(null);
  const lastTypingRef = useRef<{ index: number; at: number } | null>(null);

  useEffect(() => {
    const current = readWikiUser();
    setUser(current);
    setLoginOpen(!current);
    if (editPostId) return;
    try {
      const draft = JSON.parse(localStorage.getItem(draftKey) || '{}') as { gameKey?: string; postType?: string; subcategory?: string; title?: string; content?: string };
      if (!lockGame && draft.gameKey && availableGames.some(game => game.key === draft.gameKey)) setGameKey(draft.gameKey);
      const restoredType = defaultPostType;
      const restoredCategories = restoredType === '攻略贴'
        ? guideCategories
        : secondaryCategories[restoredType];
      setPostType(restoredType);
      setSubcategory(
        preset === 'announcement'
          ? '游戏公告'
          : preset === 'information'
            ? '活动资讯'
            : draft.subcategory && restoredCategories.includes(draft.subcategory)
              ? draft.subcategory
              : restoredCategories[0],
      );
      setTitle(draft.title || '');
      let restoredContent = rewriteHtmlImgToWebp(cleanLegacyArticleMarkup(draft.content || ''));
      if (editorRef.current) {
        editorRef.current.innerHTML = restoredContent;
        normalizeEmptyEditorBlocks(editorRef.current);
        restoredContent = editorRef.current.innerHTML;
      }
      setContent(restoredContent);
      undoHistoryRef.current = { entries: [{ html: restoredContent, selection: editorRef.current ? captureEditorSelection(editorRef.current) : null }], index: 0 };
      lastTypingRef.current = null;
      const revisions = JSON.parse(localStorage.getItem(`${draftKey}-history`) || '[]') as DraftRevision[];
      setDraftRevisions(Array.isArray(revisions) ? revisions : []);
    } catch {}
    draftHistoryReadyRef.current = true;
  }, [availableGames, defaultPostType, draftKey, editPostId, isGuide, lockGame, preset]);

  useEffect(() => {
    if (!editPostId || !user) return;
    let cancelled = false;
    const token = readWikiToken();
    if (!token) {
      setStatus('登录状态已失效，请重新登录。');
      setLoginOpen(true);
      return;
    }
    setStatus('正在加载帖子…');
    fetch(`/api/games/${encodeURIComponent(initialGameKey)}/posts/${encodeURIComponent(editPostId)}/edit`, { headers: { 'access-token': token } })
      .then(async response => {
        const payload = await response.json() as { id?: string; type?: string; category?: string; title?: string; content?: string; status?: string; error?: string; msg?: string; code?: number };
        if (!response.ok || payload.id !== editPostId || payload.status !== 'published' || typeof payload.title !== 'string' || typeof payload.content !== 'string') {
          throw new Error(payload.error || (payload.code === 404 ? '编辑接口未就绪，请重启 Go 服务。' : payload.msg) || '帖子加载失败');
        }
        return payload;
      })
      .then(post => {
        if (cancelled) return;
        const types: Record<string, (typeof postTypes)[number]> = { guide: '攻略贴', discussion: '讨论贴', news: '情报贴', creation: '创作贴' };
        setPostType(types[post.type || ''] || defaultPostType);
        let saved: { title?: string; subcategory?: string; content?: string } = {};
        try { saved = JSON.parse(localStorage.getItem(draftKey) || '{}'); } catch {}
        const nextContent = saved.content?.trim() ? saved.content : post.content || '';
        setSubcategory(saved.subcategory || post.category || defaultSubcategory);
        setTitle(saved.title?.trim() ? saved.title : post.title || '');
        let restoredContent = rewriteHtmlImgToWebp(cleanLegacyArticleMarkup(nextContent));
        if (editorRef.current) {
          editorRef.current.innerHTML = restoredContent;
          normalizeEmptyEditorBlocks(editorRef.current);
          restoredContent = editorRef.current.innerHTML;
        }
        setContent(restoredContent);
        undoHistoryRef.current = { entries: [{ html: restoredContent, selection: editorRef.current ? captureEditorSelection(editorRef.current) : null }], index: 0 };
        lastTypingRef.current = null;
        try {
          const revisions = JSON.parse(localStorage.getItem(`${draftKey}-history`) || '[]') as DraftRevision[];
          setDraftRevisions(Array.isArray(revisions) ? revisions : []);
        } catch {}
        draftHistoryReadyRef.current = true;
        setEditReady(true);
        setStatus('');
      })
      .catch(error => { if (!cancelled) setStatus(error instanceof Error ? error.message : '帖子加载失败'); });
    return () => { cancelled = true; };
  }, [defaultPostType, draftKey, editPostId, initialGameKey, user]);

  useEffect(() => {
    if (!draftHistoryReadyRef.current) return;
    const plainContent = content.replace(/<[^>]+>/g, ' ').replace(/&nbsp;|&#160;/g, ' ').replace(/\s+/g, ' ').trim();
    if (!title.trim() && !plainContent) return;
    const timer = window.setTimeout(() => {
      const signature = JSON.stringify([gameKey, postType, subcategory, title.trim(), content]);
      setDraftRevisions(previous => {
        if (previous[0]?.signature === signature) return previous;
        const changes: string[] = [];
        if (!previous.length) changes.push('开始创作');
        else {
          if (previous[0].title !== title.trim()) changes.push('修改标题');
          if (previous[0].category !== subcategory || previous[0].postType !== postType) changes.push('调整分类');
          if (previous[0].wordCount !== plainContent.length || previous[0].excerpt !== plainContent.slice(0, 80)) changes.push('修改正文');
        }
        const revision: DraftRevision = { id: `${Date.now()}`, savedAt: new Date().toISOString(), title: title.trim(), category: subcategory, postType, wordCount: plainContent.length, excerpt: plainContent.slice(0, 80), signature, changes: changes.length ? changes : ['更新内容'] };
        const next = [revision, ...previous].slice(0, 100);
        try { localStorage.setItem(`${draftKey}-history`, JSON.stringify(next)); } catch {}
        return next;
      });
      try { localStorage.setItem(draftKey, JSON.stringify({ gameKey, postType, subcategory, title, content })); } catch {}
    }, 900);
    return () => window.clearTimeout(timer);
  }, [content, draftKey, gameKey, postType, subcategory, title]);

  useEffect(() => {
    if (drawer !== 'posts' || !user) return;
    setPostsLoading(true);
    setPostsError('');
    const token = readWikiToken();
    fetch('/api/me/activity', { headers: token ? { 'access-token': token } : {} })
      .then(async response => {
        const payload = await response.json() as { error?: string; posts?: ActivityPost[] };
        if (!response.ok) throw new Error(payload.error || '发帖记录加载失败');
        return payload.posts || [];
      })
      .then(setMyPosts)
      .catch(error => setPostsError(error instanceof Error ? error.message : '发帖记录加载失败'))
      .finally(() => setPostsLoading(false));
  }, [drawer, user]);

  useEffect(() => {
    if (!drawer) return;
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') setDrawer(null); };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', close);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', close); };
  }, [drawer]);

  useEffect(() => {
    const titleElement = titleRef.current;
    if (!titleElement) return;
    const observer = new IntersectionObserver(([entry]) => {
      setTitlePinned(!entry.isIntersecting && entry.boundingClientRect.top < 68);
    }, { rootMargin: '-68px 0px 0px' });
    observer.observe(titleElement);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!editorDialog) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeEditorDialog();
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [editorDialog]);

  useEffect(() => {
    if (postType !== '攻略贴') return;
    let cancelled = false;
    fetchHomepageConfig(gameKey).then(config => {
      if (cancelled) return;
      const next = config.guideCategories.filter(category => category !== '全部');
      if (next.length) {
        setGuideCategories(next);
        setSubcategory(current => next.includes(current) ? current : next[0]);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [gameKey, postType]);

  useEffect(() => {
    if (!blockMenuOpen) return;
    const closeMenu = (event: MouseEvent) => {
      if (!blockControlsRef.current?.contains(event.target as Node)) setBlockMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setBlockMenuOpen(false);
    };
    document.addEventListener('mousedown', closeMenu);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeMenu);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [blockMenuOpen]);

  const recordEditorChange = (editor: HTMLElement) => {
    const snapshot: EditorUndoSnapshot = { html: editor.innerHTML, selection: captureEditorSelection(editor) };
    const history = undoHistoryRef.current;
    const inputKind = pendingInputKindRef.current;
    pendingInputKindRef.current = null;
    if (history.index < 0) {
      history.entries = [snapshot];
      history.index = 0;
      return;
    }
    if (history.entries[history.index].html === snapshot.html) {
      history.entries[history.index].selection = snapshot.selection;
      return;
    }
    const now = Date.now();
    const typing = lastTypingRef.current;
    if (inputKind === 'insertText' && typing?.index === history.index && now - typing.at < 750) {
      history.entries[history.index] = snapshot;
    } else {
      history.entries = history.entries.slice(0, history.index + 1);
      history.entries.push(snapshot);
      if (history.entries.length > 100) history.entries.shift();
      history.index = history.entries.length - 1;
    }
    lastTypingRef.current = inputKind === 'insertText' ? { index: history.index, at: now } : null;
  };
  const syncContent = (recordUndo = true) => {
    const editor = editorRef.current;
    if (!editor) return;
    normalizeEmptyEditorBlocks(editor);
    const headings = Array.from(editor.querySelectorAll<HTMLElement>('h1,h2,h3,h4'))
      .filter(heading => !isEmptyEditorBlock(heading))
      .map((heading, index) => {
        heading.id = heading.id || `document-section-${index + 1}`;
        return {
          id: heading.id,
          level: Number(heading.tagName.slice(1)),
          title: heading.textContent?.trim() || `未命名标题 ${index + 1}`,
        };
      });
    setOutline(headings);
    setContent(editor.innerHTML);
    if (recordUndo) recordEditorChange(editor);
    if (selectedImageRef.current) updateImageControls();
    window.requestAnimationFrame(updateBlockHandleFromCaret);
  };
  const moveEditorHistory = (direction: -1 | 1) => {
    const editor = editorRef.current;
    const history = undoHistoryRef.current;
    const nextIndex = history.index + direction;
    if (!editor || nextIndex < 0 || nextIndex >= history.entries.length) return;
    history.index = nextIndex;
    lastTypingRef.current = null;
    pendingInputKindRef.current = null;
    lastCaretClickRef.current = null;
    blockInsertionRangeRef.current = null;
    editor.innerHTML = history.entries[nextIndex].html;
    selectedImageRef.current = null;
    setImageControls(null);
    setSelectionToolbar(null);
    editor.focus({ preventScroll: true });
    savedRangeRef.current = restoreEditorSelection(editor, history.entries[nextIndex]).cloneRange();
    syncContent(false);
  };
  const updateImageControls = () => {
    const image = selectedImageRef.current;
    const shell = editorShellRef.current;
    if (!image || !shell || !image.isConnected) {
      selectedImageRef.current = null;
      setImageControls(null);
      return;
    }
    const imageRect = image.getBoundingClientRect();
    const shellRect = shell.getBoundingClientRect();
    setImageControls({
      top: imageRect.top - shellRect.top,
      left: imageRect.left - shellRect.left,
      width: imageRect.width,
      height: imageRect.height,
    });
  };
  useEffect(() => {
    window.addEventListener('resize', updateImageControls);
    window.addEventListener('scroll', updateImageControls, true);
    return () => {
      window.removeEventListener('resize', updateImageControls);
      window.removeEventListener('scroll', updateImageControls, true);
    };
  }, []);
  const selectEditorImage = (event: ReactMouseEvent<HTMLDivElement>) => {
    const target = event.target;
    const image = target instanceof HTMLImageElement ? target : null;
    if (!image || !editorRef.current?.contains(image)) {
      selectedImageRef.current = null;
      setImageControls(null);
      return;
    }
    selectedImageRef.current = image;
    setSelectionToolbar(null);
    updateImageControls();
  };
  const deleteSelectedImage = () => {
    const image = selectedImageRef.current;
    if (!image || !editorRef.current?.contains(image)) return;
    const figure = image.closest('figure');
    if (figure && editorRef.current.contains(figure)) figure.remove();
    else image.remove();
    selectedImageRef.current = null;
    setImageControls(null);
    syncContent();
  };
  const setSelectedImageWidth = (width: number) => {
    const figure = selectedImageRef.current?.closest('figure');
    if (!figure || !editorRef.current?.contains(figure)) return;
    const widthLimit = Math.max(1, editorRef.current.clientWidth);
    figure.style.width = `${Math.round(Math.min(widthLimit, Math.max(Math.min(120, widthLimit), width)))}px`;
    updateImageControls();
  };
  const startImageResize = (event: ReactPointerEvent<HTMLButtonElement>, corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right') => {
    const figure = selectedImageRef.current?.closest('figure');
    if (!figure) return;
    event.preventDefault();
    event.stopPropagation();
    const imageRect = selectedImageRef.current!.getBoundingClientRect();
    imageResizeRef.current = {
      pointerId: event.pointerId, startX: event.clientX, startY: event.clientY,
      startWidth: figure.getBoundingClientRect().width,
      aspectRatio: imageRect.height ? imageRect.width / imageRect.height : 1,
      corner,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const moveImageResize = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = imageResizeRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const horizontal = (event.clientX - drag.startX) * (drag.corner.endsWith('left') ? -1 : 1);
    const vertical = (event.clientY - drag.startY) * (drag.corner.startsWith('top') ? -1 : 1) * drag.aspectRatio;
    setSelectedImageWidth(drag.startWidth + (Math.abs(horizontal) >= Math.abs(vertical) ? horizontal : vertical));
  };
  const finishImageResize = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (imageResizeRef.current?.pointerId !== event.pointerId) return;
    imageResizeRef.current = null;
    syncContent();
  };
  const pasteContent = (event: ReactClipboardEvent<HTMLDivElement>) => {
    const clipboardSelection = window.getSelection();
    const clipboardRange = clipboardSelection?.rangeCount ? clipboardSelection.getRangeAt(0) : null;
    const pasteRange = clipboardRange && event.currentTarget.contains(clipboardRange.commonAncestorContainer)
      ? clipboardRange.cloneRange() : null;
    const html = event.clipboardData.getData('text/html');
    const text = event.clipboardData.getData('text/plain');
    const clipboardFiles = Array.from(event.clipboardData.files).length
      ? Array.from(event.clipboardData.files)
      : Array.from(event.clipboardData.items)
        .filter(item => item.kind === 'file')
        .map(item => item.getAsFile())
        .filter((file): file is File => Boolean(file));
    if (!html && !text && !clipboardFiles.length) return;
    event.preventDefault();
    if (clipboardFiles.length) {
      void uploadPastedFiles(clipboardFiles, html ? cleanPastedHtml(html) : '', pasteRange);
      return;
    }
    if (html) {
      const template = document.createElement('template');
      template.innerHTML = cleanPastedHtml(html);
      const pastedMedia = Array.from(template.content.querySelectorAll<HTMLImageElement | HTMLVideoElement>('img,video'));
      if (!pastedMedia.length) {
        command('insertHTML', template.innerHTML);
        return;
      }
      if (markDialogRange(pasteRange) === null) return;
      void (async () => {
        const failures: string[] = [];
        for (const [index, media] of pastedMedia.entries()) {
          try {
            const response = await fetch(media.src);
            if (!response.ok) throw new Error('无法读取剪贴板媒体');
            const blob = await response.blob();
            const extension = pastedMimeExtensions[blob.type.toLowerCase()];
            const mediaKind = media instanceof HTMLVideoElement ? 'video' : 'image';
            const allowedExtensions = mediaKind === 'video'
              ? ['mp4', 'm4v', 'webm', 'mov']
              : ['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif'];
            if (!extension || !allowedExtensions.includes(extension)) throw new Error('剪贴板媒体格式不支持');
            const file = new File([blob], `pasted-${mediaKind}-${Date.now()}-${index}.${extension}`, { type: blob.type });
            media.src = await uploadPastedResource(file);
          } catch {
            if (/^https?:\/\//i.test(media.src)) continue;
            failures.push(media instanceof HTMLImageElement ? media.alt || `图片 ${index + 1}` : `视频 ${index + 1}`);
            media.remove();
          }
        }
        insertHtmlAtDialogRange(template.innerHTML);
        setStatus(failures.length ? `部分粘贴媒体上传失败：${failures.join('、')}` : '粘贴媒体已上传并插入。');
      })();
      return;
    }
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    if (lines.length < 2) {
      command('insertHTML', escapePastedText(text));
      return;
    }

    const editor = editorRef.current;
    const selection = window.getSelection();
    const liveRange = selection?.rangeCount ? selection.getRangeAt(0) : null;
    const range = liveRange && editor?.contains(liveRange.commonAncestorContainer)
      ? liveRange.cloneRange()
      : savedRangeRef.current?.cloneRange();
    if (!editor || !selection || !range || !editor.contains(range.commonAncestorContainer)) return;
    range.deleteContents();
    range.collapse(true);
    const anchor = range.startContainer.nodeType === Node.ELEMENT_NODE
      ? range.startContainer as HTMLElement
      : range.startContainer.parentElement;
    let paragraph = anchor?.closest<HTMLParagraphElement>('p');
    if (!paragraph || !editor.contains(paragraph)) {
      selection.removeAllRanges();
      selection.addRange(range);
      savedRangeRef.current = range.cloneRange();
      command('insertHTML', lines.map(line => `<p>${escapePastedText(line) || '<br>'}</p>`).join(''));
      return;
    }

    const tailRange = document.createRange();
    tailRange.selectNodeContents(paragraph);
    tailRange.setStart(range.startContainer, range.startOffset);
    const trailingContent = tailRange.extractContents();
    if (isEmptyEditorBlock(paragraph) || paragraph.querySelector(':scope > br:only-child')) paragraph.replaceChildren();
    const makeLine = (line: string) => {
      const block = document.createElement('p');
      if (line) block.appendChild(document.createTextNode(line));
      else block.appendChild(document.createElement('br'));
      return block;
    };

    if (lines[0]) paragraph.appendChild(document.createTextNode(lines[0]));
    else if (!paragraph.childNodes.length) paragraph.appendChild(document.createElement('br'));
    let lastParagraph = paragraph;
    lines.slice(1).forEach(line => {
      const nextParagraph = makeLine(line);
      lastParagraph.parentNode?.insertBefore(nextParagraph, lastParagraph.nextSibling);
      lastParagraph = nextParagraph;
    });
    const caretOffset = lastParagraph.childNodes.length;
    if (trailingContent.hasChildNodes()) {
      if (lastParagraph.querySelector(':scope > br:only-child')) lastParagraph.replaceChildren();
      lastParagraph.appendChild(trailingContent);
    }
    const caret = document.createRange();
    caret.setStart(lastParagraph, Math.min(caretOffset, lastParagraph.childNodes.length));
    caret.collapse(true);
    selection.removeAllRanges();
    selection.addRange(caret);
    savedRangeRef.current = caret.cloneRange();
    syncContent();
  };
  const handleEditorKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if ((event.ctrlKey || event.metaKey) && !event.altKey) {
      const key = event.key.toLowerCase();
      if (key === 'z' || key === 'y') {
        event.preventDefault();
        moveEditorHistory(key === 'y' || event.shiftKey ? 1 : -1);
        return;
      }
    }
    if ((event.key === 'Backspace' || event.key === 'Delete') &&
      selectedImageRef.current && editorRef.current?.contains(selectedImageRef.current)) {
      event.preventDefault();
      deleteSelectedImage();
      return;
    }
    if (event.key === 'Enter') {
      if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;
      const editor = editorRef.current;
      const selection = window.getSelection();
      if (!editor || !selection?.isCollapsed || !selection.rangeCount) return;
      const range = selection.getRangeAt(0);
      if (!editor.contains(range.commonAncestorContainer)) return;
      const anchor = range.startContainer.nodeType === Node.ELEMENT_NODE
        ? range.startContainer as HTMLElement
        : range.startContainer.parentElement;
      const paragraph = anchor?.closest<HTMLParagraphElement>('p');
      if (!paragraph || !editor.contains(paragraph)) return;

      event.preventDefault();
      if (isEmptyEditorBlock(paragraph)) {
        const nextParagraph = document.createElement('p');
        nextParagraph.appendChild(document.createElement('br'));
        paragraph.parentNode?.insertBefore(nextParagraph, paragraph.nextSibling);
        const nextCaret = document.createRange();
        nextCaret.setStart(nextParagraph, 0);
        nextCaret.collapse(true);
        selection.removeAllRanges();
        selection.addRange(nextCaret);
        savedRangeRef.current = nextCaret.cloneRange();
      } else {
        // Keep normal Enter to one visual line. Native contentEditable Enter creates
        // paragraph blocks, whose margins make each line look like a blank line.
        document.execCommand('insertLineBreak');
        savedRangeRef.current = selection.getRangeAt(0).cloneRange();
      }
      syncContent();
      return;
    }
    if (event.key !== 'Backspace' && event.key !== 'Delete') return;
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection?.isCollapsed || !selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;
    const anchor = range.startContainer.nodeType === Node.ELEMENT_NODE
      ? range.startContainer as HTMLElement
      : range.startContainer.parentElement;
    const callout = anchor?.closest<HTMLElement>('.document-callout');
    if (!callout || !editor.contains(callout)) return;
    const hasContent = Boolean(callout.textContent?.replace(/\u200b/g, '').trim()) ||
      Boolean(callout.querySelector('img,video,audio,table,hr,input,iframe'));
    if (hasContent) return;

    event.preventDefault();
    const nextBlock = callout.nextElementSibling;
    const previousBlock = callout.previousElementSibling;
    if (event.key === 'Backspace') {
      const emptyLine = document.createElement('p');
      emptyLine.appendChild(document.createElement('br'));
      callout.replaceWith(emptyLine);
      const caret = document.createRange();
      caret.setStart(emptyLine, 0);
      caret.collapse(true);
      selection.removeAllRanges();
      selection.addRange(caret);
    } else {
      callout.remove();
      let caretBlock = nextBlock || previousBlock;
      if (!caretBlock) {
        caretBlock = document.createElement('p');
        caretBlock.appendChild(document.createElement('br'));
        editor.appendChild(caretBlock);
      }
      const caret = document.createRange();
      caret.selectNodeContents(caretBlock);
      caret.collapse(caretBlock !== previousBlock);
      selection.removeAllRanges();
      selection.addRange(caret);
    }
    savedRangeRef.current = selection.getRangeAt(0).cloneRange();
    syncContent();
  };
  const rememberSelection = () => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (editor.contains(range.commonAncestorContainer)) savedRangeRef.current = range.cloneRange();
  };
  const restoreSelection = () => {
    const editor = editorRef.current;
    const range = savedRangeRef.current;
    if (!editor || !range || !editor.contains(range.commonAncestorContainer)) return;
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  };
  const updateBlockHandleFromCaret = () => {
    if (editorPointerDownRef.current) return;
    const editor = editorRef.current;
    const shell = editorShellRef.current;
    const selection = window.getSelection();
    if (!editor || !shell || !selection?.rangeCount) {
      setBlockHandlePosition(null);
      return;
    }
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) {
      setBlockHandlePosition(null);
      return;
    }
    savedRangeRef.current = range.cloneRange();
    if (!selection.isCollapsed) {
      selectedTextRangeRef.current = range.cloneRange();
      setBlockHandlePosition(null);
      return;
    }
    const activeBlock = directEditorBlock(editor, range.startContainer, range.startOffset);
    if (activeBlock && isAtomicEditorBlock(activeBlock)) {
      setBlockHandlePosition(null);
      return;
    }
    let rangeRect = range.getBoundingClientRect();
    if (!rangeRect.height) {
      // A collapsed range after an Enter-created <br> often has no rectangle.
      // Measure a temporary inline caret at that exact DOM position instead of
      // falling back to the top of the whole paragraph.
      const marker = document.createElement('span');
      marker.style.cssText = 'display:inline-block;width:0;height:1em;overflow:hidden;vertical-align:baseline;pointer-events:none';
      range.cloneRange().insertNode(marker);
      rangeRect = marker.getBoundingClientRect();
      const restored = document.createRange();
      restored.setStartBefore(marker);
      restored.collapse(true);
      selection.removeAllRanges();
      selection.addRange(restored);
      marker.remove();
      savedRangeRef.current = selection.getRangeAt(0).cloneRange();
    }
    const shellRect = shell.getBoundingClientRect();
    const editorRect = editor.getBoundingClientRect();
    const activeColumn = activeBlock?.closest<HTMLElement>('.document-columns > div');
    const controlLeft = activeColumn
      ? activeColumn.getBoundingClientRect().left - editorRect.left - 44
      : -44;
    const editorStyle = window.getComputedStyle(editor);
    const editorPaddingTop = Number.parseFloat(editorStyle.paddingTop) || 0;
    const editorLineHeight = Number.parseFloat(editorStyle.lineHeight) || 24;
    const fallbackTop = editorRect.top - shellRect.top + editorPaddingTop + editorLineHeight / 2;
    const lastClick = lastCaretClickRef.current;
    if (lastClick && lastClick.node === range.startContainer && lastClick.offset === range.startOffset) {
      const blockRect = activeBlock?.getBoundingClientRect() || editorRect;
      const lineHeight = Number.parseFloat(window.getComputedStyle(activeBlock || editor).lineHeight) || editorLineHeight;
      const line = Math.max(0, Math.floor((lastClick.clientY - blockRect.top) / lineHeight));
      const lineCenter = blockRect.top + Math.min((line + 0.5) * lineHeight, Math.max(lineHeight / 2, blockRect.height - lineHeight / 2));
      setBlockHandlePosition({ top: lineCenter - shellRect.top, left: controlLeft });
      return;
    }
    if (activeBlock) {
      if (rangeRect.height) {
        setBlockHandlePosition({ top: rangeRect.top - shellRect.top + rangeRect.height / 2, left: controlLeft });
        return;
      }
      const blockRect = activeBlock.getBoundingClientRect();
      setBlockHandlePosition({ top: blockRect.top - shellRect.top + Math.max(6, Math.min(blockRect.height / 2, 14)), left: controlLeft });
      return;
    }
    setBlockHandlePosition({ top: rangeRect.height ? rangeRect.top - shellRect.top + rangeRect.height / 2 : fallbackTop, left: controlLeft });
  };
  const updateSelectionToolbar = () => {
    const editor = editorRef.current;
    const shell = editorShellRef.current;
    const selection = window.getSelection();
    if (!editor || !shell || !selection?.rangeCount || selection.isCollapsed) {
      setSelectionToolbar(null);
      setSelectionMenu(null);
      selectedTextRangeRef.current = null;
      return;
    }
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) {
      setSelectionToolbar(null);
      setSelectionMenu(null);
      selectedTextRangeRef.current = null;
      return;
    }
    const selectionRects = range.getClientRects();
    const anchorRect = selectionRects[selectionRects.length - 1] || range.getBoundingClientRect();
    if (!anchorRect.width && !anchorRect.height) return;
    const shellRect = shell.getBoundingClientRect();
    const toolbarHalfWidth = 215;
    const naturalLeft = anchorRect.left - shellRect.left + anchorRect.width / 2;
    setSelectionToolbar({
      top: anchorRect.bottom - shellRect.top + 8,
      left: Math.min(Math.max(toolbarHalfWidth, naturalLeft), Math.max(toolbarHalfWidth, shellRect.width - toolbarHalfWidth)),
    });
    savedRangeRef.current = range.cloneRange();
    selectedTextRangeRef.current = range.cloneRange();
  };
  useEffect(() => {
    const finishPointerSelection = () => { editorPointerDownRef.current = false; };
    window.addEventListener('mouseup', finishPointerSelection);
    return () => window.removeEventListener('mouseup', finishPointerSelection);
  }, []);
  useEffect(() => {
    const handleSelectionChange = () => window.requestAnimationFrame(() => {
      if (editorPointerDownRef.current) return;
      updateSelectionToolbar();
      updateBlockHandleFromCaret();
    });
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);
  const command = (name: string, value?: string) => {
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand(name, false, value);
    syncContent();
    rememberSelection();
  };
  const insertList = (kind: 'ordered' | 'unordered') => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    restoreSelection();
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    const anchor = range.startContainer.nodeType === Node.ELEMENT_NODE
      ? range.startContainer as HTMLElement
      : range.startContainer.parentElement;
    const paragraph = anchor?.closest<HTMLParagraphElement>('p');
    if (!paragraph || !editor.contains(paragraph) || !paragraph.contains(range.endContainer)) {
      command(kind === 'ordered' ? 'insertOrderedList' : 'insertUnorderedList');
      return;
    }

    const list = document.createElement(kind === 'ordered' ? 'ol' : 'ul');
    const item = document.createElement('li');
    list.appendChild(item);
    if (range.collapsed) {
      while (paragraph.firstChild) item.appendChild(paragraph.firstChild);
    } else {
      const prefixRange = document.createRange();
      prefixRange.selectNodeContents(paragraph);
      prefixRange.setEnd(range.startContainer, range.startOffset);
      const prefix = prefixRange.cloneContents();
      const selected = range.extractContents();
      const suffixRange = document.createRange();
      suffixRange.selectNodeContents(paragraph);
      suffixRange.setStart(range.startContainer, range.startOffset);
      const suffix = suffixRange.cloneContents();
      item.appendChild(selected);

      const createParagraph = (content: DocumentFragment) => {
        const block = paragraph.cloneNode(false) as HTMLParagraphElement;
        block.appendChild(content);
        return block;
      };
      const hasVisibleContent = (fragment: DocumentFragment) =>
        Boolean(fragment.textContent?.replace(/\u200b/g, '').trim()) ||
        Boolean(fragment.querySelector('img,video,audio,table,hr,input,iframe'));
      const before = hasVisibleContent(prefix) ? createParagraph(prefix) : null;
      const after = hasVisibleContent(suffix) ? createParagraph(suffix) : null;
      const parent = paragraph.parentNode;
      if (!parent) return;
      if (before) parent.insertBefore(before, paragraph);
      parent.insertBefore(list, paragraph);
      if (after) parent.insertBefore(after, paragraph);
      paragraph.remove();
      const caret = document.createRange();
      caret.selectNodeContents(item);
      caret.collapse(false);
      selection.removeAllRanges();
      selection.addRange(caret);
      savedRangeRef.current = caret.cloneRange();
      syncContent();
      return;
    }
    paragraph.replaceWith(list);
    const caret = document.createRange();
    caret.selectNodeContents(item);
    caret.collapse(false);
    selection.removeAllRanges();
    selection.addRange(caret);
    savedRangeRef.current = caret.cloneRange();
    syncContent();
  };
  const runSelectionCommand = (name: string, value?: string) => {
    if (selectedTextRangeRef.current) savedRangeRef.current = selectedTextRangeRef.current.cloneRange();
    setSelectionMenu(null);
    command(name, value);
    window.requestAnimationFrame(updateSelectionToolbar);
  };
  const setSelectionFontSize = (size: number) => {
    const editor = editorRef.current;
    if (!editor || !selectedTextRangeRef.current) return;
    savedRangeRef.current = selectedTextRangeRef.current.cloneRange();
    editor.focus();
    restoreSelection();
    // execCommand splits the selection across paragraphs and existing inline marks.
    // Replace its temporary HTML font tags with exact pixel sizes before saving.
    document.execCommand('fontSize', false, '7');
    editor.querySelectorAll<HTMLFontElement>('font[size="7"]').forEach(font => {
      const span = document.createElement('span');
      span.style.fontSize = `${size}px`;
      while (font.firstChild) span.appendChild(font.firstChild);
      font.replaceWith(span);
    });
    setSelectionMenu(null);
    syncContent();
    rememberSelection();
    window.requestAnimationFrame(updateSelectionToolbar);
  };
  const insertInlineCode = () => {
    if (selectedTextRangeRef.current) savedRangeRef.current = selectedTextRangeRef.current.cloneRange();
    restoreSelection();
    const selectedText = window.getSelection()?.toString() || '';
    if (!selectedText) return;
    const escapedText = selectedText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    runSelectionCommand('insertHTML', `<code class="document-inline-code">${escapedText}</code>`);
  };
  const runBlockAction = (action: () => void) => {
    setBlockMenuOpen(false);
    action();
  };
  const safeUrl = (value: string) => {
    try { const url = new URL(value); return ['http:', 'https:'].includes(url.protocol) ? url.href : ''; } catch { return ''; }
  };
  const markDialogRange = (insertionRange?: Range | null) => {
    const editor = editorRef.current;
    const selectedRange = selectedTextRangeRef.current;
    if (editor && insertionRange && editor.contains(insertionRange.commonAncestorContainer)) {
      savedRangeRef.current = insertionRange.cloneRange();
    } else if (editor && selectedRange && !selectedRange.collapsed && editor.contains(selectedRange.commonAncestorContainer)) {
      savedRangeRef.current = selectedRange.cloneRange();
    } else {
      rememberSelection();
    }
    const range = savedRangeRef.current?.cloneRange();
    if (!editor || !range || !editor.contains(range.commonAncestorContainer)) return null;
    const selectedText = range.toString();
    const start = document.createComment('dialog-range-start');
    const end = document.createComment('dialog-range-end');
    if (range.collapsed) {
      range.insertNode(start);
      const afterStart = document.createRange();
      afterStart.setStartAfter(start);
      afterStart.collapse(true);
      afterStart.insertNode(end);
    } else {
      const endRange = range.cloneRange();
      endRange.collapse(false);
      endRange.insertNode(end);
      const startRange = range.cloneRange();
      startRange.collapse(true);
      startRange.insertNode(start);
    }
    dialogRangeMarkersRef.current = { start, end };
    return selectedText;
  };
  const insertLink = () => {
    const selectedText = markDialogRange();
    if (selectedText === null) return;
    setEditorDialog({ kind: 'link', label: selectedText, url: 'https://' });
  };
  const discardDialogRangeMarkers = () => {
    dialogRangeMarkersRef.current?.start.remove();
    dialogRangeMarkersRef.current?.end.remove();
    dialogRangeMarkersRef.current = null;
  };
  useEffect(() => {
    const clearMarkers = () => {
      dialogRangeMarkersRef.current?.start.remove();
      dialogRangeMarkersRef.current?.end.remove();
      dialogRangeMarkersRef.current = null;
    };
    const inputs = [imageInputRef.current, fileInputRef.current].filter((input): input is HTMLInputElement => Boolean(input));
    inputs.forEach(input => input.addEventListener('cancel', clearMarkers));
    return () => inputs.forEach(input => input.removeEventListener('cancel', clearMarkers));
  }, []);
  const closeEditorDialog = () => {
    discardDialogRangeMarkers();
    blockInsertionRangeRef.current = null;
    tableScrollPositionRef.current = null;
    setEditorDialog(null);
  };
  const insertLinkAtSavedRange = (label: string, url: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    const scrollPosition = captureScrollPosition(editor);
    editor.focus({ preventScroll: true });
    const markers = dialogRangeMarkersRef.current;
    let range: Range;
    if (markers && editor.contains(markers.start) && editor.contains(markers.end)) {
      range = document.createRange();
      range.setStartAfter(markers.start);
      range.setEndBefore(markers.end);
    } else {
      range = savedRangeRef.current?.cloneRange() || document.createRange();
    }
    if (!editor.contains(range.commonAncestorContainer)) {
      range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
    }
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.textContent = label;
    range.deleteContents();
    range.insertNode(anchor);
    discardDialogRangeMarkers();
    range.setStartAfter(anchor);
    range.collapse(true);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    savedRangeRef.current = range.cloneRange();
    selectedTextRangeRef.current = null;
    setSelectionToolbar(null);
    syncContent();
    const restoreScroll = () => restoreScrollPosition(scrollPosition);
    restoreScroll();
    window.requestAnimationFrame(() => {
      restoreScroll();
      window.requestAnimationFrame(restoreScroll);
    });
  };
  const insertHtmlAtDialogRange = (html: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const markers = dialogRangeMarkersRef.current;
    let range: Range;
    if (markers && editor.contains(markers.start) && editor.contains(markers.end)) {
      range = document.createRange();
      range.setStartAfter(markers.start);
      range.setEndBefore(markers.end);
    } else {
      range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
    }
    const fragment = range.createContextualFragment(html);
    const lastInsertedNode = fragment.lastChild;
    range.deleteContents();
    range.insertNode(fragment);
    discardDialogRangeMarkers();
    if (lastInsertedNode?.parentNode) {
      range.setStartAfter(lastInsertedNode);
      range.collapse(true);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      savedRangeRef.current = range.cloneRange();
    }
    selectedTextRangeRef.current = null;
    setSelectionToolbar(null);
    syncContent();
  };
  const insertBlockAtDialogRange = (html: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    const scrollPosition = tableScrollPositionRef.current;
    if (scrollPosition) editor.focus({ preventScroll: true });
    else editor.focus();

    const template = document.createElement('template');
    template.innerHTML = html;
    const fragment = template.content;
    const markers = dialogRangeMarkersRef.current;
    let insertionRange: Range | null = null;
    if (markers && editor.contains(markers.start) && editor.contains(markers.end)) {
      const selectedRange = document.createRange();
      selectedRange.setStartAfter(markers.start);
      selectedRange.setEndBefore(markers.end);
      selectedRange.deleteContents();

      let hostBlock = markers.start.parentElement;
      while (hostBlock && hostBlock !== editor && !/^(P|H1|H2|H3|H4|BLOCKQUOTE)$/.test(hostBlock.tagName)) {
        hostBlock = hostBlock.parentElement;
      }
      const textBlock = hostBlock instanceof HTMLElement && hostBlock !== editor && /^(P|H1|H2|H3|H4|BLOCKQUOTE)$/.test(hostBlock.tagName)
        ? hostBlock
        : null;
      const cell = markers.start.parentElement?.closest<HTMLTableCellElement>('td,th');
      if (!textBlock && cell && editor.contains(cell)) {
        markers.end.remove();
        const trailingRange = document.createRange();
        trailingRange.setStartAfter(markers.start);
        trailingRange.setEnd(cell, cell.childNodes.length);
        const trailing = trailingRange.extractContents();
        const leadingRange = document.createRange();
        leadingRange.setStart(cell, 0);
        leadingRange.setEndBefore(markers.start);
        const leading = leadingRange.extractContents();
        markers.start.remove();

        const hasContent = (content: DocumentFragment) => Boolean(content.textContent?.replace(/\u200b/g, '').trim()) ||
          Boolean(content.querySelector('img,video,audio,table,hr,input,iframe'));
        cell.replaceChildren();
        if (hasContent(leading)) {
          const leadingParagraph = document.createElement('p');
          leadingParagraph.appendChild(leading);
          cell.appendChild(leadingParagraph);
        }
        const lastInsertedNode = fragment.lastChild;
        cell.appendChild(fragment);
        if (hasContent(trailing)) {
          const trailingParagraph = document.createElement('p');
          trailingParagraph.appendChild(trailing);
          cell.appendChild(trailingParagraph);
        }
        if (lastInsertedNode?.parentNode) {
          insertionRange = document.createRange();
          insertionRange.setStartAfter(lastInsertedNode);
        }
      } else if (textBlock?.parentNode) {
        const host = textBlock;
        markers.end.remove();
        const trailingRange = document.createRange();
        trailingRange.setStartAfter(markers.start);
        trailingRange.setEnd(host, host.childNodes.length);
        const trailing = trailingRange.extractContents();
        const leadingRange = document.createRange();
        leadingRange.setStart(host, 0);
        leadingRange.setEndBefore(markers.start);
        const leading = leadingRange.extractContents();
        markers.start.remove();

        const hasLeading = Boolean(leading.textContent?.replace(/\u200b/g, '').trim()) ||
          Boolean(leading.querySelector('img,video,audio,table,hr,input,iframe'));
        const hasTrailing = Boolean(trailing.textContent?.replace(/\u200b/g, '').trim()) ||
          Boolean(trailing.querySelector('img,video,audio,table,hr,input,iframe'));
        if (hasLeading) host.appendChild(leading);
        const lastInsertedNode = fragment.lastChild;
        const parent = host.parentNode;
        if (!parent) return;
        parent.insertBefore(fragment, hasLeading ? host.nextSibling : host);
        if (!hasLeading) host.remove();
        if (hasTrailing && lastInsertedNode) {
          const trailingBlock = host.cloneNode(false) as HTMLElement;
          trailingBlock.appendChild(trailing);
          parent.insertBefore(trailingBlock, lastInsertedNode.nextSibling);
        }
        insertionRange = document.createRange();
        if (lastInsertedNode?.parentNode) insertionRange.setStartAfter(lastInsertedNode);
      } else {
        let hostNode: Node | null = markers.start;
        while (hostNode?.parentNode && hostNode.parentNode !== editor) hostNode = hostNode.parentNode;
        const host = hostNode instanceof HTMLElement && hostNode.parentNode === editor ? hostNode : null;
        const lastInsertedNode = fragment.lastChild;
        if (host?.parentElement === editor) {
          markers.start.remove();
          markers.end.remove();
          editor.insertBefore(fragment, host.nextSibling);
          if (lastInsertedNode?.parentNode) {
            insertionRange = document.createRange();
            insertionRange.setStartAfter(lastInsertedNode);
          }
        } else {
          const point = document.createRange();
          point.setStartAfter(markers.start);
          point.collapse(true);
          markers.start.remove();
          markers.end.remove();
          point.insertNode(fragment);
          if (lastInsertedNode?.parentNode) {
            insertionRange = document.createRange();
            insertionRange.setStartAfter(lastInsertedNode);
          }
        }
      }
      discardDialogRangeMarkers();
    } else {
      const lastInsertedNode = fragment.lastChild;
      editor.appendChild(fragment);
      if (lastInsertedNode?.parentNode) {
        insertionRange = document.createRange();
        insertionRange.setStartAfter(lastInsertedNode);
      }
      discardDialogRangeMarkers();
    }

    if (insertionRange) {
      insertionRange.collapse(true);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(insertionRange);
      savedRangeRef.current = insertionRange.cloneRange();
    }
    selectedTextRangeRef.current = null;
    setSelectionToolbar(null);
    syncContent();
    if (scrollPosition) {
      const restore = () => restoreScrollPosition(scrollPosition);
      restore();
      window.requestAnimationFrame(() => {
        restore();
        window.requestAnimationFrame(restore);
      });
      tableScrollPositionRef.current = null;
    }
  };
  const insertHtmlAtCurrentSelection = (html: string) => {
    if (markDialogRange() === null) return;
    insertHtmlAtDialogRange(html);
  };
  const openMediaPicker = (input: HTMLInputElement | null) => {
    if (!input || markDialogRange() === null) return;
    input.click();
  };
  const insertTask = () =>
    insertHtmlAtCurrentSelection('<p class="document-task"><input type="checkbox"> <span>待办事项</span></p><p><br></p>');
  const insertCode = () =>
    insertHtmlAtCurrentSelection('<pre><code>在这里输入代码</code></pre><p><br></p>');
  const insertDivider = () => insertHtmlAtCurrentSelection('<hr><p><br></p>');
  const insertTable = () => {
    const editor = editorRef.current;
    if (!editor) return;
    tableScrollPositionRef.current = captureScrollPosition(editor);
    if (markDialogRange(blockInsertionRangeRef.current) === null) { tableScrollPositionRef.current = null; return; }
    blockInsertionRangeRef.current = null;
    setEditorDialog({ kind: 'table', rows: '3', columns: '3' });
  };
  const insertColumns = () => {
    if (markDialogRange() === null) return;
    insertBlockAtDialogRange('<div class="document-columns"><div contenteditable="true" data-placeholder="左侧内容"></div><div contenteditable="true" data-placeholder="右侧内容"></div></div><p><br></p>');
  };
  const insertCallout = () => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    const liveRange = selection?.rangeCount ? selection.getRangeAt(0) : null;
    const blockRange = blockInsertionRangeRef.current;
    const range = blockRange && editor?.contains(blockRange.commonAncestorContainer)
      ? blockRange
      : liveRange && editor?.contains(liveRange.commonAncestorContainer)
        ? liveRange
        : savedRangeRef.current;
    if (!editor || !range || !editor.contains(range.startContainer)) return;
    const anchor = range.startContainer.nodeType === Node.ELEMENT_NODE
      ? range.startContainer as HTMLElement
      : range.startContainer.parentElement;
    const callout = anchor?.closest<HTMLElement>('.document-callout');
    if (!callout || !editor.contains(callout)) {
      if (markDialogRange(range) === null) return;
      insertHtmlAtDialogRange('<aside class="document-callout"><strong>提示</strong><p>在这里补充需要重点说明的内容。</p></aside><p><br></p>');
      blockInsertionRangeRef.current = null;
      return;
    }

    const keepSelection = !range.collapsed && callout.contains(range.endContainer);
    const endMarker = keepSelection ? document.createComment('callout-selection-end') : null;
    if (endMarker) {
      const end = range.cloneRange();
      end.collapse(false);
      end.insertNode(endMarker);
    }
    const start = range.cloneRange();
    start.collapse(true);
    const startMarker = document.createComment('callout-selection-start');
    start.insertNode(startMarker);
    const content = document.createDocumentFragment();
    Array.from(callout.childNodes).forEach(node => {
      if (node instanceof HTMLElement && node.tagName === 'STRONG') {
        const paragraph = document.createElement('p');
        paragraph.appendChild(node);
        content.appendChild(paragraph);
      } else content.appendChild(node);
    });
    callout.replaceWith(content);
    if (startMarker.parentNode) {
      const nextRange = document.createRange();
      if (endMarker?.parentNode) {
        nextRange.setStartAfter(startMarker);
        nextRange.setEndBefore(endMarker);
      } else {
        nextRange.setStartBefore(startMarker);
        nextRange.collapse(true);
      }
      startMarker.remove();
      endMarker?.remove();
      selection?.removeAllRanges();
      selection?.addRange(nextRange);
      savedRangeRef.current = nextRange.cloneRange();
      selectedTextRangeRef.current = keepSelection ? nextRange.cloneRange() : null;
    }
    blockInsertionRangeRef.current = null;
    if (!keepSelection) setSelectionToolbar(null);
    syncContent();
    if (keepSelection) window.requestAnimationFrame(updateSelectionToolbar);
  };
  const applySelectionCallout = () => {
    const editor = editorRef.current;
    const range = selectedTextRangeRef.current?.cloneRange();
    if (!editor || !range || range.collapsed || !editor.contains(range.commonAncestorContainer)) return;
    const anchor = range.startContainer.nodeType === Node.ELEMENT_NODE
      ? range.startContainer as HTMLElement
      : range.startContainer.parentElement;
    const callout = anchor?.closest<HTMLElement>('.document-callout');
    setSelectionMenu(null);
    if (callout && callout.contains(range.endContainer)) {
      blockInsertionRangeRef.current = null;
      savedRangeRef.current = range;
      restoreSelection();
      insertCallout();
      return;
    }
    const wrapper = document.createElement('aside');
    wrapper.className = 'document-callout';
    wrapper.appendChild(range.cloneContents());
    runSelectionCommand('insertHTML', wrapper.outerHTML);
  };
  const insertButton = () => {
    if (markDialogRange() === null) return;
    setEditorDialog({ kind: 'button', label: '查看详情', url: 'https://' });
  };
  const confirmEditorDialog = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editorDialog) return;
    if (editorDialog.kind === 'link') {
      const label = editorDialog.label.trim();
      const url = safeUrl(editorDialog.url.trim());
      if (!label || !url) { setEditorDialog({ ...editorDialog, error: '请填写链接文字和有效的 http 或 https 链接。' }); return; }
      insertLinkAtSavedRange(label, url);
      setEditorDialog(null);
      return;
    }
    if (editorDialog.kind === 'table') {
      const requestedRows = Number(editorDialog.rows);
      const requestedColumns = Number(editorDialog.columns);
      if (!Number.isInteger(requestedRows) || !Number.isInteger(requestedColumns) || requestedRows < 2 || requestedRows > 12 || requestedColumns < 2 || requestedColumns > 8) {
        setEditorDialog({ ...editorDialog, error: '行数需为 2–12，列数需为 2–8。' });
        return;
      }
      const body = Array.from({ length: requestedRows }, (_, row) =>
        `<tr>${Array.from({ length: requestedColumns }, (_, column) => `<${row ? 'td' : 'th'}>${row ? '内容' : `标题 ${column + 1}`}</${row ? 'td' : 'th'}>`).join('')}</tr>`,
      ).join('');
      insertBlockAtDialogRange(`<div class="document-table-wrap"><table><tbody>${body}</tbody></table></div><p><br></p>`);
      setEditorDialog(null);
      return;
    }
    const label = editorDialog.label.trim();
    const url = safeUrl(editorDialog.url.trim());
    if (!label || !url) { setEditorDialog({ ...editorDialog, error: '请填写按钮文字和有效的 http 或 https 链接。' }); return; }
    insertHtmlAtDialogRange(`<p><a class="document-button" href="${url}">${label.replace(/[<>]/g, '')}</a></p><p><br></p>`);
    setEditorDialog(null);
  };
  const uploadPostResource = async (file: File) => {
    const token = readWikiToken();
    if (!token) throw new Error('登录状态已失效，请重新登录。');
    const cdnUrl = await uploadToObs({
      file,
      gameDomain,
      onProgress: (percent) => setStatus(`正在上传 ${file.name}（${percent}%）`),
    });
    const url = safeUrl(cdnUrl);
    if (!url) throw new Error('上传服务返回了无效的资源地址。');
    return url;
  };
  const uploadPastedResource = async (file: File) => {
    const token = readWikiToken();
    if (!token) throw new Error('登录状态已失效，请重新登录。');
    setStatus(`正在上传 ${file.name}…`);
    const form = new FormData();
    form.append('file', file);
    const response = await fetch(`/api/games/${encodeURIComponent(gameKey)}/post-resources`, {
      method: 'POST', headers: { 'access-token': token }, body: form,
    });
    const payload = await response.json() as { url?: string; error?: string };
    if (!response.ok || !payload.url) throw new Error(payload.error || '剪贴板图片上传失败');
    const url = safeUrl(payload.url);
    if (!url) throw new Error('上传服务返回了无效的资源地址。');
    return url;
  };
  const normalizePastedFile = (file: File, index: number) => {
    const currentExtension = file.name.split('.').pop()?.toLowerCase() || '';
    if (pastedResourceExtensions.has(currentExtension)) return file;
    const inferredExtension = pastedMimeExtensions[file.type.toLowerCase()];
    if (!inferredExtension) return null;
    return new File([file], `pasted-resource-${Date.now()}-${index}.${inferredExtension}`, {
      type: file.type,
      lastModified: file.lastModified,
    });
  };
  const pastedResourceMarkup = (file: File, url: string) => {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    const escapedUrl = escapeHtml(url);
    const escapedName = escapeHtml(file.name);
    if (/^(jpe?g|png|webp|gif|avif)$/.test(extension)) {
      return `<figure><img src="${escapedUrl}" alt=""><figcaption></figcaption></figure><p><br></p>`;
    }
    if (/^(mp4|m4v|webm|mov)$/.test(extension)) {
      return `<figure><video controls preload="metadata" src="${escapedUrl}"></video><figcaption>${escapedName}</figcaption></figure><p><br></p>`;
    }
    return `<p class="document-attachment"><a href="${escapedUrl}" target="_blank" rel="noopener noreferrer">📎 ${escapedName}</a></p><p><br></p>`;
  };
  const uploadPastedFiles = async (files: File[], pastedHtml = '', pasteRange: Range | null = null) => {
    if (markDialogRange(pasteRange) === null) return;
    const snippets: string[] = [];
    const failures: string[] = [];
    for (const [index, originalFile] of files.entries()) {
      const file = normalizePastedFile(originalFile, index);
      if (!file) {
        failures.push(originalFile.name || `文件 ${index + 1}（格式不支持）`);
        continue;
      }
      try {
        const url = await uploadPastedResource(file);
        snippets.push(pastedResourceMarkup(file, url));
      } catch (error) {
        failures.push(`${file.name || `文件 ${index + 1}`}：${error instanceof Error ? error.message : '上传失败'}`);
      }
    }
    if (pastedHtml) {
      const template = document.createElement('template');
      template.innerHTML = pastedHtml;
      template.content.querySelectorAll('img').forEach(image => image.remove());
      insertHtmlAtDialogRange(`${template.innerHTML}${snippets.join('')}`);
    } else if (snippets.length) {
      insertBlockAtDialogRange(snippets.join(''));
    } else {
      discardDialogRangeMarkers();
    }
    if (failures.length) setStatus(`部分剪贴板资源上传失败：${failures.join('、')}`);
    else if (snippets.length) setStatus('剪贴板资源已上传并插入。');
  };
  const uploadAndInsert = async (file: File, kind: 'image' | 'video' | 'file') => {
    try {
      const url = await uploadPostResource(file);
      const escapedUrl = escapeHtml(url);
      const escapedName = escapeHtml(file.name);
      if (kind === 'image') {
        insertBlockAtDialogRange(`<figure><img src="${escapedUrl}" alt=""><figcaption>${escapedName}</figcaption></figure><p><br></p>`);
      } else if (kind === 'video') {
        insertBlockAtDialogRange(`<figure><video controls preload="metadata" src="${escapedUrl}"></video><figcaption>${escapedName}</figcaption></figure><p><br></p>`);
      } else {
        insertHtmlAtDialogRange(`<p class="document-attachment"><a href="${escapedUrl}" target="_blank" rel="noopener noreferrer">📎 ${escapedName}</a></p><p><br></p>`);
      }
      setStatus('资源上传完成。');
    } catch (error) {
      discardDialogRangeMarkers();
      setStatus(error instanceof Error ? error.message : '资源上传失败，请重试。');
    }
  };
  const insertImage = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) { discardDialogRangeMarkers(); return; }
    if (!/\.(jpe?g|png|webp|gif|avif)$/i.test(file.name)) { discardDialogRangeMarkers(); setStatus('请选择 JPG、PNG、WebP、GIF 或 AVIF 图片。'); return; }
    void uploadAndInsert(file, 'image');
  };
  const insertFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) { discardDialogRangeMarkers(); return; }
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (/^(jpe?g|png|webp|gif|avif)$/.test(extension)) { void uploadAndInsert(file, 'image'); return; }
    if (/^(mp4|m4v|webm|mov)$/.test(extension)) { void uploadAndInsert(file, 'video'); return; }
    void uploadAndInsert(file, 'file');
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (editPostId && !editReady) { setStatus('帖子尚未加载完成。'); return; }
    if (!user) { setLoginOpen(true); return; }
    if (!editorRef.current?.textContent?.trim()) { setStatus('请填写正文内容。'); editorRef.current?.focus(); return; }
    // Empty captions add a visibly blank line under images in the published article.
    // Remove captions with no readable text while preserving any non-empty markup.
    const publishContent = editorRef.current.cloneNode(true) as HTMLElement;
    publishContent.querySelectorAll('.document-columns > div[data-placeholder]').forEach(column => {
      column.removeAttribute('contenteditable');
      column.removeAttribute('data-placeholder');
    });
    publishContent.querySelectorAll('figure figcaption').forEach(caption => {
      if (!caption.textContent?.trim()) caption.remove();
    });
    const editorContent = publishContent.innerHTML;
    setContent(editorContent);
    const token = readWikiToken();
    if (!token) { setStatus('登录状态已失效，请重新登录。'); setLoginOpen(true); return; }
    const typeMap = { '攻略贴':'guide', '讨论贴':'discussion', '情报贴':'news', '创作贴':'creation' } as const;
    setStatus('正在提交…');
    try {
      const response = await fetch(editPostId
        ? `/api/games/${encodeURIComponent(gameKey)}/posts/${encodeURIComponent(editPostId)}`
        : `/api/games/${encodeURIComponent(gameKey)}/posts`, {
        method:editPostId ? 'PUT' : 'POST',
        headers:{'content-type':'application/json','access-token':token},
        body:JSON.stringify({ type:isGuide ? 'guide' : typeMap[postType], category:subcategory, title, content:editorContent }),
      });
      const responseText = await response.text();
      let payload: {id?:string;status?:string;error?:string} = {};
      try { payload = responseText ? JSON.parse(responseText) : {}; } catch { payload = { error: responseText || `服务器返回异常（HTTP ${response.status}）` }; }
      if (!response.ok) throw new Error(payload.error || '提交失败');
      if (editPostId && (payload.id !== editPostId || payload.status !== 'published')) throw new Error('保存接口未返回有效结果，请确认 Go 服务已更新。');
      localStorage.removeItem(draftKey);
      localStorage.removeItem(`${draftKey}-history`);
      setStatus(editPostId ? '修改已保存。' : payload.status === 'published' ? '发布成功。' : '已提交审核，通过后会正式展示。');
      window.setTimeout(() => {
        window.location.href = editPostId
          ? wikiGuidePostPath(gameDomain, editPostId)
          : isGuide
            ? wikiGuidesPath(gameDomain)
            : wikiShellPath(gameDomain, 'dcommunity');
      }, 900);
    } catch (error) { setStatus(error instanceof Error ? error.message : '提交失败'); }
  };

  const contentText = content
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&#160;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const outlineBaseLevel = outline.length ? Math.min(...outline.map(item => item.level)) : 1;

  return <main className="create-post-page">
    <header className="create-post-topbar">
      <div className="create-post-context" aria-label="发布位置">
        <a className="create-post-crumb" href={wikiGameHomePath(gameDomain)} title="返回当前游戏 Wiki 首页">
          <span>{availableGames.find(game => game.key === gameKey)?.name || gameKey}</span>
        </a>
        <span className="create-post-crumb-separator" aria-hidden="true">›</span>
        <a className="create-post-crumb" href={primaryChannelURL} title={`前往${primaryChannel}页面`}>
          <span>{primaryChannel}</span>
        </a>
      </div>
      <span className={`create-post-brand${titlePinned && title.trim() ? ' is-document-title' : ' is-empty'}`} title={titlePinned && title.trim() ? title.trim() : undefined}>{titlePinned && title.trim() ? title.trim() : ''}</span>
      <div className="create-post-actions">
        <button className="primary" type="submit" form="create-post-form" disabled={!editReady}><FileText/>{editPostId ? '保存修改' : `发布${isGuide ? '攻略' : preset === 'announcement' ? '公告' : '帖子'}`}</button>
      </div>
    </header>
    <section className="create-post-shell">
      <form id="create-post-form" onSubmit={submit}>
        <div className={`create-post-workspace${outline.length ? ' has-outline' : ''}`}>
          <section className="document-compose">
            <input ref={titleRef} className="document-title" aria-label="标题" value={title} onChange={event => setTitle(event.target.value)} maxLength={80} placeholder="请输入标题" required/>
            <label className="document-classifier document-title-category">
              <select aria-label="帖子二级分类" value={subcategory} onChange={event => setSubcategory(event.target.value)}>
                {availableSecondary.map(category => <option key={category}>{category}</option>)}
              </select>
            </label>
            <div className="document-editor" ref={editorShellRef}>
              {blockHandlePosition !== null && <div className="document-block-controls" ref={blockControlsRef} style={{ top: blockHandlePosition.top, left: blockHandlePosition.left }}>
                <button className="document-block-add" type="button" aria-label="插入内容块" aria-expanded={blockMenuOpen} onMouseDown={event => { event.preventDefault(); restoreSelection(); blockInsertionRangeRef.current = savedRangeRef.current?.cloneRange() || null; }} onClick={() => setBlockMenuOpen(open => !open)}><Plus/></button>
                {blockMenuOpen && <div className="document-block-menu" role="menu" aria-label="插入内容">
                  <strong>基础</strong>
                  <div className="document-block-basic" aria-label="基础格式">
                    <button type="button" title="一级标题" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(() => command('formatBlock', 'h1'))}>H1</button>
                    <button type="button" title="二级标题" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(() => command('formatBlock', 'h2'))}>H2</button>
                    <button type="button" title="三级标题" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(() => command('formatBlock', 'h3'))}>H3</button>
                    <button type="button" title="四级标题" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(() => command('formatBlock', 'h4'))}>H4</button>
                    <button type="button" title="有序列表" aria-label="有序列表" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(() => insertList('ordered'))}><ListOrdered/></button>
                    <button type="button" title="无序列表" aria-label="无序列表" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(() => insertList('unordered'))}><List/></button>
                    <button type="button" title="代码块" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(insertCode)}>{'{ }'}</button>
                    <button type="button" title="引用" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(() => command('formatBlock', 'blockquote'))}>❝</button>
                    <button type="button" title="分割线" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(insertDivider)}>—</button>
                    <button type="button" title="链接" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(insertLink)}><Link2/></button>
                    <button type="button" title="加粗" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(() => command('bold'))}><Bold/></button>
                    <button type="button" title="斜体" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(() => command('italic'))}><Italic/></button>
                  </div>
                  <strong>常用</strong>
                  <button type="button" role="menuitem" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(insertTask)}><span>☑</span><span><b>任务</b><small>添加可勾选的待办事项</small></span></button>
                  <button type="button" role="menuitem" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(() => openMediaPicker(imageInputRef.current))}><ImagePlus/><span><b>图片</b><small>上传本地图片</small></span></button>
                  <button type="button" role="menuitem" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(() => openMediaPicker(fileInputRef.current))}><Video/><span><b>视频或文件</b><small>插入视频或附件</small></span></button>
                  <button type="button" role="menuitem" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(insertTable)}><span>▦</span><span><b>表格</b><small>创建自定义行列的表格</small></span></button>
                  <button type="button" role="menuitem" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(insertColumns)}><span>▥</span><span><b>分栏</b><small>将内容分成左右两栏</small></span></button>
                  <button type="button" role="menuitem" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(insertCallout)}><Highlighter/><span><b>高亮块</b><small>突出显示重要信息</small></span></button>
                  <button type="button" role="menuitem" onMouseDown={event => event.preventDefault()} onClick={() => runBlockAction(insertButton)}><span>▻</span><span><b>按钮</b><small>添加带链接的按钮</small></span></button>
                </div>}
              </div>}
              <input ref={imageInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif,.avif" hidden onChange={insertImage}/>
              <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif,.avif,.mp4,.m4v,.webm,.mov,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip" hidden onChange={insertFile}/>
              {selectionToolbar && <div className="document-selection-toolbar" style={{ top: selectionToolbar.top, left: selectionToolbar.left }} role="toolbar" aria-label="文字格式">
                <div className="selection-tool-menu-wrap">
                  <button type="button" title="文字样式" aria-expanded={selectionMenu === 'style'} onMouseDown={event => event.preventDefault()} onClick={() => setSelectionMenu(menu => menu === 'style' ? null : 'style')}><span className="selection-type-icon">T</span><ChevronDown/></button>
                  {selectionMenu === 'style' && <div className="selection-tool-dropdown selection-style-menu">
                    <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('formatBlock', 'p')}>正文</button>
                    <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('formatBlock', 'h1')}>一级标题</button>
                    <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('formatBlock', 'h2')}>二级标题</button>
                    <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('formatBlock', 'h3')}>三级标题</button>
                    <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('formatBlock', 'blockquote')}>引用</button>
                    <button type="button" onMouseDown={event => event.preventDefault()} onClick={applySelectionCallout}><Highlighter/>高亮块</button>
                    <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('insertOrderedList')}><ListOrdered/>有序列表</button>
                    <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('insertUnorderedList')}><List/>无序列表</button>
                  </div>}
                </div>
                <span/>
                <div className="selection-tool-menu-wrap">
                  <button type="button" title="对齐方式" aria-expanded={selectionMenu === 'align'} onMouseDown={event => event.preventDefault()} onClick={() => setSelectionMenu(menu => menu === 'align' ? null : 'align')}><AlignLeft/><ChevronDown/></button>
                  {selectionMenu === 'align' && <div className="selection-tool-dropdown selection-align-menu">
                    <button type="button" title="左对齐" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('justifyLeft')}><AlignLeft/>左对齐</button>
                    <button type="button" title="居中" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('justifyCenter')}><AlignCenter/>居中</button>
                    <button type="button" title="右对齐" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('justifyRight')}><AlignRight/>右对齐</button>
                    <button type="button" title="两端对齐" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('justifyFull')}><AlignJustify/>两端对齐</button>
                  </div>}
                </div>
                <span/>
                <div className="selection-tool-menu-wrap">
                  <button type="button" title="字号" aria-label="调整字号" aria-expanded={selectionMenu === 'size'} onMouseDown={event => event.preventDefault()} onClick={() => setSelectionMenu(menu => menu === 'size' ? null : 'size')}><span className="selection-size-icon">tT</span></button>
                  {selectionMenu === 'size' && <div className="selection-tool-dropdown selection-size-menu">
                    {[24, 20, 18, 16, 14, 12].map(size => <button type="button" key={size} onMouseDown={event => event.preventDefault()} onClick={() => setSelectionFontSize(size)}>{size}px</button>)}
                  </div>}
                </div>
                <button type="button" title="加粗" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('bold')}><Bold/></button>
                <button type="button" title="删除线" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('strikeThrough')}><Strikethrough/></button>
                <button type="button" title="斜体" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('italic')}><Italic/></button>
                <button type="button" title="下划线" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('underline')}><Underline/></button>
                <button type="button" title="添加链接" onMouseDown={event => event.preventDefault()} onClick={() => { if (selectedTextRangeRef.current) savedRangeRef.current = selectedTextRangeRef.current.cloneRange(); insertLink(); }}><Link2/></button>
                <button type="button" title="行内代码" onMouseDown={event => event.preventDefault()} onClick={insertInlineCode}><Code2/></button>
                <div className="selection-tool-menu-wrap">
                  <button className="selection-color-trigger" type="button" title="文字颜色与高亮" aria-expanded={selectionMenu === 'color'} onMouseDown={event => event.preventDefault()} onClick={() => setSelectionMenu(menu => menu === 'color' ? null : 'color')}><span>A</span><ChevronDown/></button>
                  {selectionMenu === 'color' && <div className="selection-tool-dropdown selection-color-menu">
                    <strong>文字颜色</strong>
                    <div className="selection-text-colors">{selectionTextColors.map(color => <button type="button" aria-label={`文字颜色 ${color.name}`} title={color.name} style={{ '--selection-color': color.value } as CSSProperties} key={color.value} onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('foreColor', color.value)}><span>A</span></button>)}</div>
                    <strong>背景高亮</strong>
                    <div className="selection-highlight-colors">{selectionHighlightColors.map(color => <button className={color.value === 'transparent' ? 'is-transparent' : ''} type="button" aria-label={`背景颜色 ${color.name}`} title={color.name} style={{ '--selection-color': color.value } as CSSProperties} key={color.name} onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('hiliteColor', color.value)}/>)}</div>
                    <button className="selection-clear-format" type="button" onMouseDown={event => event.preventDefault()} onClick={() => runSelectionCommand('removeFormat')}>恢复默认</button>
                  </div>}
                </div>
              </div>}
              {/* A rich contentEditable needs a textbox role; a textarea cannot contain the document HTML. */}
              {/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role */}
              <div id="document-canvas" ref={editorRef} className="document-canvas" role="textbox" aria-label="正文" aria-multiline="true" tabIndex={0} contentEditable suppressContentEditableWarning data-placeholder="写下你想分享的内容……" onBeforeInput={event => { const inputKind = (event.nativeEvent as InputEvent).inputType; if (inputKind === 'historyUndo' || inputKind === 'historyRedo') { event.preventDefault(); moveEditorHistory(inputKind === 'historyUndo' ? -1 : 1); return; } pendingInputKindRef.current = inputKind || null; const editor = editorRef.current; const history = undoHistoryRef.current; if (editor && history.index >= 0) history.entries[history.index].selection = captureEditorSelection(editor); }} onInput={() => { lastCaretClickRef.current = null; syncContent(); }} onKeyDown={event => { editorPointerDownRef.current = false; lastCaretClickRef.current = null; handleEditorKeyDown(event); }} onPaste={pasteContent} onMouseDown={() => { editorPointerDownRef.current = true; lastTypingRef.current = null; }} onMouseUp={event => { editorPointerDownRef.current = false; const selection = window.getSelection(); const range = selection?.rangeCount ? selection.getRangeAt(0) : null; lastCaretClickRef.current = range && selection?.isCollapsed && editorRef.current?.contains(range.startContainer) ? { node: range.startContainer, offset: range.startOffset, clientY: event.clientY } : null; selectEditorImage(event); updateBlockHandleFromCaret(); window.requestAnimationFrame(updateSelectionToolbar); }} onKeyUp={() => { updateBlockHandleFromCaret(); window.requestAnimationFrame(updateSelectionToolbar); }} onBlurCapture={event => { const column = event.target instanceof HTMLElement ? event.target.closest<HTMLElement>('.document-columns > div[data-placeholder]') : null; if (column && !column.textContent?.trim() && !column.querySelector('img,video,table,hr')) { column.innerHTML = ''; syncContent(); } }} onFocus={updateBlockHandleFromCaret}/>
              {imageControls && <div className="document-image-selection" style={{ top: imageControls.top, left: imageControls.left, width: imageControls.width, height: imageControls.height }}>
                <button type="button" className="document-image-delete" aria-label="删除图片" title="删除图片" onMouseDown={event => event.preventDefault()} onClick={deleteSelectedImage}>×</button>
                {selectedImageRef.current?.closest('figure') && (['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const).map(corner => <button type="button" key={corner} className={`document-image-resize-handle is-${corner}`} aria-label={`从${{ 'top-left': '左上角', 'top-right': '右上角', 'bottom-left': '左下角', 'bottom-right': '右下角' }[corner]}调整图片大小`} title="拖动调整图片大小" onPointerDown={event => startImageResize(event, corner)} onPointerMove={moveImageResize} onPointerUp={finishImageResize} onPointerCancel={finishImageResize} onKeyDown={event => {
                  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                  event.preventDefault();
                  setSelectedImageWidth((selectedImageRef.current?.closest('figure')?.getBoundingClientRect().width || imageControls.width) + (event.key === 'ArrowRight' ? 20 : -20));
                  syncContent();
                }}/>) }
              </div>}
            </div>
            {status && <output className="create-post-status">{status}</output>}
          </section>
          <aside className="document-left-rail" aria-label="写作辅助">
            <div className="document-outline" aria-label="文章目录" hidden={!outline.length}>
              <strong>目录</strong>
              <nav>
                {outline.map(item => <button type="button" style={{ '--outline-level': item.level - outlineBaseLevel + 1 } as CSSProperties} key={item.id} onClick={() => document.getElementById(item.id)?.scrollIntoView({ behavior:'smooth', block:'center' })}>{item.title}</button>)}
              </nav>
            </div>
            <div className="document-assistant" aria-label="创作辅助">
              <section><strong>创作状态</strong><p>{contentText.length} 字 · 约 {Math.max(1, Math.ceil(contentText.length / 450))} 分钟读完</p><div className="document-assistant-actions"><button type="button" onClick={() => setDrawer('posts')}><FileText/>我的发帖</button><button type="button" onClick={() => setDrawer('revisions')}><History/>修改记录</button></div></section>
            </div>
          </aside>
        </div>
      </form>
    </section>
    {editorDialog && <><button className="document-dialog-backdrop" type="button" aria-label="关闭弹窗" onClick={closeEditorDialog}/><section className="document-dialog" role="dialog" aria-modal="true" aria-labelledby="document-dialog-title">
      <form onSubmit={confirmEditorDialog}>
        <header><div><small>INSERT CONTENT</small><h2 id="document-dialog-title">{editorDialog.kind === 'link' ? '添加链接' : editorDialog.kind === 'table' ? '插入表格' : '添加按钮'}</h2></div><button type="button" aria-label="关闭" onClick={closeEditorDialog}><X/></button></header>
        <div className="document-dialog-fields">
          {editorDialog.kind === 'link' && <><label htmlFor="document-dialog-link-label">链接文字<input id="document-dialog-link-label" autoFocus value={editorDialog.label} maxLength={200} placeholder="请输入显示文字" onChange={event => setEditorDialog(current => current?.kind === 'link' ? { ...current, label: event.target.value, error: undefined } : current)}/></label><label htmlFor="document-dialog-link-url">链接地址<input id="document-dialog-link-url" inputMode="url" value={editorDialog.url} placeholder="https://example.com" onChange={event => setEditorDialog(current => current?.kind === 'link' ? { ...current, url: event.target.value, error: undefined } : current)}/><small>仅支持 http 或 https 链接</small></label></>}
          {editorDialog.kind === 'table' && <div className="document-dialog-field-row"><label htmlFor="document-dialog-rows">行数<input id="document-dialog-rows" autoFocus type="number" min="2" max="12" value={editorDialog.rows} onChange={event => setEditorDialog(current => current?.kind === 'table' ? { ...current, rows: event.target.value, error: undefined } : current)}/><small>2–12 行</small></label><label htmlFor="document-dialog-columns">列数<input id="document-dialog-columns" type="number" min="2" max="8" value={editorDialog.columns} onChange={event => setEditorDialog(current => current?.kind === 'table' ? { ...current, columns: event.target.value, error: undefined } : current)}/><small>2–8 列</small></label></div>}
          {editorDialog.kind === 'button' && <><label htmlFor="document-dialog-button-label">按钮文字<input id="document-dialog-button-label" autoFocus value={editorDialog.label} maxLength={30} placeholder="查看详情" onChange={event => setEditorDialog(current => current?.kind === 'button' ? { ...current, label: event.target.value, error: undefined } : current)}/></label><label htmlFor="document-dialog-button-url">按钮链接<input id="document-dialog-button-url" inputMode="url" value={editorDialog.url} placeholder="https://example.com" onChange={event => setEditorDialog(current => current?.kind === 'button' ? { ...current, url: event.target.value, error: undefined } : current)}/><small>仅支持 http 或 https 链接</small></label></>}
          {editorDialog.error && <p className="document-dialog-error" role="alert">{editorDialog.error}</p>}
        </div>
        <footer><button type="button" onClick={closeEditorDialog}>取消</button><button className="primary" type="submit">{editorDialog.kind === 'link' ? '添加链接' : editorDialog.kind === 'table' ? '插入表格' : '添加按钮'}</button></footer>
      </form>
    </section></>}
    {drawer && <><button className="document-drawer-backdrop" type="button" aria-label="关闭侧边抽屉" onClick={() => setDrawer(null)}/><aside className="document-drawer" role="dialog" aria-modal="true" aria-labelledby="document-drawer-title">
      <header><div><small>{drawer === 'posts' ? 'MY POSTS' : 'REVISION HISTORY'}</small><h2 id="document-drawer-title">{drawer === 'posts' ? '我的发帖' : '修改记录'}</h2></div><button type="button" aria-label="关闭" onClick={() => setDrawer(null)}><X/></button></header>
      <div className="document-drawer-body">
        {drawer === 'posts' && (!user ? <div className="document-drawer-empty"><FileText/><strong>登录后查看发帖</strong><p>登录后，这里会显示你发布和待审核的全部内容。</p><button type="button" onClick={() => setLoginOpen(true)}>立即登录</button></div> : postsLoading ? <div className="document-drawer-empty"><strong>正在加载发帖…</strong></div> : postsError ? <div className="document-drawer-empty"><strong>加载失败</strong><p>{postsError}</p></div> : myPosts.length ? <div className="document-drawer-list">{myPosts.map(post => <div className="document-drawer-post" key={`${post.gameKey}:${post.id}`}>
          <a href={wikiGuidePostPath(domainForGameKey(post.gameKey), post.id)}><span>{post.type === 'guide' ? '攻略' : '帖子'} · {post.status === 'published' ? '已发布' : post.status === 'pending' ? '待审核' : '草稿'}</span><strong>{post.title || '未命名帖子'}</strong>{post.excerpt && <p>{post.excerpt}</p>}<time>{new Date(post.updatedAt).toLocaleString('zh-CN')}</time></a>
          {post.status === 'published' && <a className="document-drawer-post-edit" href={wikiGuideEditPath(domainForGameKey(post.gameKey), post.id)}>编辑</a>}
        </div>)}</div> : <div className="document-drawer-empty"><FileText/><strong>还没有发帖</strong><p>发布后的内容会集中显示在这里。</p></div>)}
        {drawer === 'revisions' && (draftRevisions.length ? <ol className="document-revision-list">{draftRevisions.map((revision, index) => <li key={revision.id}><i/><div><span>{index === 0 ? '当前版本' : `历史版本 ${draftRevisions.length - index}`}</span><strong>{revision.changes.join('、')}</strong><p>{revision.title || '未填写标题'} · {revision.category} · {revision.wordCount} 字</p>{revision.excerpt && <blockquote>{revision.excerpt}</blockquote>}<time>{new Date(revision.savedAt).toLocaleString('zh-CN')}</time></div></li>)}</ol> : <div className="document-drawer-empty"><History/><strong>暂无修改记录</strong><p>开始输入后，标题、分类和正文的变化会自动记录。</p></div>)}
      </div>
    </aside></>}
    <CommentLogin open={loginOpen} onOpenChange={open => { setLoginOpen(open); if (!open && !readWikiUser()) window.location.href = isGuide ? wikiGuidesPath(gameDomain) : wikiShellPath(gameDomain, 'dcommunity'); }} onLogin={next => { writeWikiUser(next); setUser(next); setLoginOpen(false); }}/>
  </main>;
}
